/**
 * Las zonas de la aplicacion: que hay, quien las ve y en que URL viven.
 *
 * ===========================================================================
 * POR QUE LA PANTALLA UNICA DEJO DE SERVIR
 *
 * Hasta aqui todo pasaba en un sitio y se cambiaba con botones de la cabecera.
 * Con cuatro trabajos distintos --cotizar, mantener el catalogo, revisar
 * proveedores, administrar-- eso deja de ser una pantalla y pasa a ser un cajon:
 * quien entra por primera vez no sabe cuantas cosas hay, y quien busca una
 * concreta tiene que acordarse de que boton la abria.
 *
 * Un menu lateral contesta las dos preguntas a la vez. Lo que se ve es lo que
 * hay; lo que no se ve es lo que este usuario no puede hacer.
 *
 * ===========================================================================
 * UNA TABLA, Y LA MISMA FORMA QUE `permisos.ts`
 *
 * Es deliberado que este archivo se parezca al de la API: alli una tabla dice
 * que capacidad tiene cada rol, aqui una tabla dice que capacidad abre cada
 * zona. Ninguna de las dos deduce nada de la otra --las capacidades llegan
 * RESUELTAS en `GET /sesion`-- y por eso anadir un rol no obliga a tocar este
 * archivo, ni anadir una zona a tocar aquel.
 *
 * Esto NO es control de acceso. Una zona que no aparece en el menu sigue siendo
 * alcanzable escribiendo su URL a mano, y lo que hay detras es la API, que
 * contesta 403. El menu evita ensenar puertas que no abren; no es la cerradura.
 *
 * ===========================================================================
 * LA URL ES PARTE DE LA APLICACION
 *
 * Cada zona tiene ruta propia (`#/cotizaciones`) por tres motivos concretos que
 * antes no se cumplian: recargar la pagina devolvia siempre al buscador; el
 * boton de atras del navegador salia de la aplicacion entera; y no habia forma
 * de mandarle a alguien "mira esto" por chat.
 *
 * Se usa el fragmento (`#/...`) y no History API a proposito: el bundle se
 * sirve como un archivo estatico [frontend/servidor.mjs], y con rutas reales
 * cada recarga en `/cotizaciones` seria un 404 del servidor de estaticos salvo
 * que se le anada un reescritor. El fragmento no llega al servidor.
 */

import type { Capacidad } from './api';

export type Zona =
  | 'cotizar'
  | 'cotizaciones'
  | 'rfq'
  | 'tarifas'
  | 'proveedores'
  | 'administracion'
  | 'ayuda';

export interface DefinicionZona {
  readonly id: Zona;
  /** El fragmento de URL. Sin el `#`, que lo pone `rutaDeZona`. */
  readonly ruta: string;
  /**
   * Cualquiera de estas abre la zona. Vacio = la ve todo el mundo.
   *
   * Es "cualquiera" y no "todas" porque Administracion la abren dos trabajos
   * distintos --gestionar usuarios y fijar margenes-- que no siempre estan en
   * el mismo rol: un SUPERVISOR entra por los margenes y no ve la pestana de
   * usuarios, y un ADMIN entra por las dos.
   */
  readonly capacidades: readonly Capacidad[];
}

/**
 * El orden es el del menu, y no es alfabetico ni casual: va del trabajo diario
 * al que se hace una vez al mes. Un agente encuentra lo suyo en las dos
 * primeras filas y no vuelve a bajar la vista.
 */
export const ZONAS: readonly DefinicionZona[] = [
  { id: 'cotizar', ruta: '/cotizar', capacidades: ['catalogo.consultar'] },
  { id: 'cotizaciones', ruta: '/cotizaciones', capacidades: ['cotizaciones.ver'] },
  { id: 'rfq', ruta: '/rfq', capacidades: ['rfq.gestionar'] },
  { id: 'tarifas', ruta: '/tarifas', capacidades: ['tarifas.gestionar'] },
  { id: 'proveedores', ruta: '/proveedores', capacidades: ['proveedores.gestionar'] },
  {
    id: 'administracion',
    ruta: '/administracion',
    capacidades: ['usuarios.gestionar', 'margenes.gestionar'],
  },
  // La ayuda no pide nada. Es la unica zona que existe para quien todavia no
  // sabe que puede hacer, asi que condicionarla seria al reves.
  { id: 'ayuda', ruta: '/ayuda', capacidades: [] },
];

export function definicionDe(zona: Zona): DefinicionZona {
  const d = ZONAS.find((z) => z.id === zona);
  // No puede pasar: `Zona` es un union cerrado sobre esta misma tabla. El
  // throw existe para que si algun dia se anade un valor al tipo y no a la
  // tabla, falle aqui y no con un `undefined` tres capas mas abajo.
  if (d === undefined) throw new Error(`Zona sin definir: ${zona}`);
  return d;
}

export function abre(zona: DefinicionZona, capacidades: readonly Capacidad[]): boolean {
  return (
    zona.capacidades.length === 0 || zona.capacidades.some((c) => capacidades.includes(c))
  );
}

export function zonasVisibles(
  capacidades: readonly Capacidad[],
): readonly DefinicionZona[] {
  return ZONAS.filter((z) => abre(z, capacidades));
}

/**
 * La zona en la que arranca alguien que entra sin pedir una concreta.
 *
 * La primera que puede abrir, no `cotizar` a secas: un OPERADOR si tiene
 * `catalogo.consultar` --y por tanto arranca en el buscador, que es lo que
 * quiere--, pero el dia que exista un rol que no lo tenga, aterrizaria en una
 * zona vacia sin saber por que.
 *
 * `ayuda` no pide capacidades, asi que siempre queda al menos una.
 */
export function zonaInicial(capacidades: readonly Capacidad[]): Zona {
  return zonasVisibles(capacidades)[0].id;
}

export function rutaDeZona(zona: Zona): string {
  return `#${definicionDe(zona).ruta}`;
}

/**
 * Que zona pide una URL. `null` si no la reconoce.
 *
 * Tolerante con la barra final y con el `#` ausente, que es como llegan las
 * URLs pegadas a mano en un chat. Insensible a mayusculas por lo mismo.
 * Cualquier otra cosa es `null` y quien llama decide: aqui NO se cae a
 * `cotizar` en silencio, porque una URL que no existe y la portada se verian
 * igual y nadie se enteraria de que el enlace estaba mal.
 */
export function zonaDeRuta(hash: string): Zona | null {
  const limpia = hash.replace(/^#/, '').replace(/\/+$/, '').toLowerCase();
  const con = limpia.startsWith('/') ? limpia : `/${limpia}`;
  return ZONAS.find((z) => z.ruta === con)?.id ?? null;
}
